import { UploadFailure } from "./upload-failure";

export const UPLOAD_STALL_TIMEOUT_MS = 2 * 60 * 1000;

export type PresignedUpload = {
	uploadUrl: string;
	method: "PUT";
	requiredHeaders: { "Content-Type": string; "Content-Length": string };
	providerKey: string;
	allocationToken: string;
};

function readRetryAfterMs(xhr: XMLHttpRequest): number | undefined {
	const header = xhr.getResponseHeader?.("Retry-After");
	if (!header) return undefined;
	const seconds = Number(header);
	return Number.isFinite(seconds) && seconds > 0 ? seconds * 1000 : undefined;
}

function xmlField(body: string, field: string): string | undefined {
	return new RegExp(`<${field}>([^<]*)</${field}>`).exec(body)?.[1];
}

export function putToPresignedUrl(
	upload: PresignedUpload,
	file: File,
	onProgress?: (percent: number) => void
): Promise<void> {
	return new Promise((resolve, reject) => {
		const xhr = new XMLHttpRequest();
		let stallTimer: ReturnType<typeof setTimeout> | undefined;
		let stalled = false;

		const clearStallTimer = (): void => {
			if (stallTimer !== undefined) clearTimeout(stallTimer);
			stallTimer = undefined;
		};
		const armStallTimer = (): void => {
			clearStallTimer();
			stallTimer = setTimeout(() => {
				stalled = true;
				xhr.abort();
			}, UPLOAD_STALL_TIMEOUT_MS);
		};

		xhr.open(upload.method, upload.uploadUrl, true);
		xhr.setRequestHeader("Content-Type", upload.requiredHeaders["Content-Type"]);
		xhr.upload.onprogress = (event) => {
			armStallTimer();
			if (onProgress && event.lengthComputable)
				onProgress(Math.round((event.loaded / event.total) * 100));
		};
		xhr.onprogress = () => armStallTimer();
		xhr.onload = () => {
			clearStallTimer();
			if (xhr.status >= 200 && xhr.status < 300) {
				resolve();
				return;
			}
			const body = typeof xhr.responseText === "string" ? xhr.responseText : "";
			const code = xmlField(body, "Code");
			const message = xmlField(body, "Message") ?? xhr.statusText;
			reject(
				new UploadFailure(`Upload failed: ${xhr.status} ${message}`, {
					status: xhr.status,
					code,
					retryAfterMs: readRetryAfterMs(xhr)
				})
			);
		};
		xhr.onerror = () => {
			clearStallTimer();
			reject(
				new UploadFailure("Upload failed: network error", { code: "connection_failure" })
			);
		};
		xhr.ontimeout = () => {
			clearStallTimer();
			reject(
				new UploadFailure("Upload failed: response timeout", { code: "response_timeout" })
			);
		};
		xhr.onabort = () => {
			clearStallTimer();
			reject(
				stalled
					? new UploadFailure(
							`Upload failed: no progress for ${UPLOAD_STALL_TIMEOUT_MS / 1000}s`,
							{ code: "response_timeout" }
						)
					: new UploadFailure("Upload failed: broken connection", {
							code: "broken_connection"
						})
			);
		};
		armStallTimer();
		xhr.send(file);
	});
}
